import React from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Typography,
    Progress,
    Tooltip,
    Badge,
} from 'antd';
import {
    PlayCircleOutlined,
    HistoryOutlined,
    LockOutlined,
    CheckCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { cronToHumanReadable } from '../../utils/cronUtils';

const { Text } = Typography;

interface RegisteredJob {
    jobName: string;
    cronExpression: string;
    description?: string;
    defaultTimeoutMs?: number;
    isLocked?: boolean;
    currentLock?: {
        jobName: string;
        lockedBy: string;
        lockedAt: string;
        expiresAt: string;
        executionCount: number;
        lastSuccessfulAt?: string;
        lastError?: string;
        lastErrorAt?: string;
        isExpired?: boolean;
    };
    statistics?: {
        jobName: string;
        totalExecutions: number;
        successRate: number;
        averageDurationMs: number;
        lastExecution?: any;
        recentHistory?: any[];
    };
}

interface RegisteredJobsTableProps {
    registeredJobs: RegisteredJob[];
    onRunJob: (jobName: string) => void;
    onViewHistory: (jobName: string) => void;
    isRunning: boolean;
}

export const RegisteredJobsTable: React.FC<RegisteredJobsTableProps> = ({
    registeredJobs,
    onRunJob,
    onViewHistory,
    isRunning,
}) => {
    const formatDuration = (ms: number) => {
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
    };

    const getSuccessRateColor = (rate: number) => {
        if (rate >= 90) return '#52c41a';
        if (rate >= 70) return '#faad14';
        return '#ff4d4f';
    };

    const columns = [
        {
            title: 'Job Name',
            dataIndex: 'jobName',
            key: 'jobName',
            render: (text: string, record: RegisteredJob) => (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text strong style={{ fontFamily: 'monospace' }}>
                            {text}
                        </Text>
                        {record.isLocked && (
                            <Badge
                                status={record.currentLock?.isExpired ? 'error' : 'processing'}
                                text={record.currentLock?.isExpired ? 'Expired Lock' : 'Locked'}
                            />
                        )}
                    </div>
                    {record.description && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            {record.description}
                        </Text>
                    )}
                </div>
            ),
        },
        {
            title: 'Schedule',
            dataIndex: 'cronExpression',
            key: 'cronExpression',
            render: (text: string) => {
                const humanReadable = cronToHumanReadable(text);
                return (
                    <div>
                        <div style={{ marginBottom: '2px' }}>
                            <Text strong>{humanReadable}</Text>
                        </div>
                        <Text code style={{ fontSize: '11px', color: '#8c8c8c' }}>
                            {text}
                        </Text>
                    </div>
                );
            },
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: any, record: RegisteredJob) => {
                if (record.isLocked) {
                    if (record.currentLock?.isExpired) {
                        return <Tag color="red" icon={<LockOutlined />}>Expired Lock</Tag>;
                    }
                    return <Tag color="orange" icon={<LockOutlined />}>Locked</Tag>;
                }
                return <Tag color="green" icon={<CheckCircleOutlined />}>Available</Tag>;
            },
        },
        {
            title: 'Statistics',
            key: 'statistics',
            render: (_: any, record: RegisteredJob) => {
                const stats = record.statistics;
                if (!stats || stats.totalExecutions === 0) {
                    return <Text type="secondary">No executions</Text>;
                }

                return (
                    <div>
                        <div style={{ marginBottom: 4 }}>
                            <Text style={{ fontSize: '12px' }}>
                                {stats.totalExecutions} runs
                            </Text>
                        </div>
                        <Tooltip title={`Success Rate: ${stats.successRate.toFixed(1)}%`}>
                            <Progress
                                percent={stats.successRate}
                                size="small"
                                strokeColor={getSuccessRateColor(stats.successRate)}
                                showInfo={false}
                                style={{ width: 80 }}
                            />
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            title: 'Avg Duration',
            key: 'avgDuration',
            render: (_: any, record: RegisteredJob) => {
                const stats = record.statistics;
                if (!stats || stats.totalExecutions === 0) {
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <Text style={{ fontSize: '12px' }}>
                        {formatDuration(stats.averageDurationMs)}
                    </Text>
                );
            },
        },
        {
            title: 'Last Execution',
            key: 'lastExecution',
            render: (_: any, record: RegisteredJob) => {
                const stats = record.statistics;
                if (!stats?.lastExecution) {
                    return <Text type="secondary">-</Text>;
                }

                const lastExecution = stats.lastExecution;
                const date = moment(lastExecution.startedAt);

                return (
                    <div>
                        <Tooltip title={date.format('YYYY-MM-DD HH:mm:ss')}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                {date.fromNow()}
                            </Text>
                        </Tooltip>
                        <div>
                            <Tag
                                color={lastExecution.status === 'SUCCESS' ? 'green' : 'red'}
                                style={{ fontSize: '10px', padding: '0 4px' }}
                            >
                                {lastExecution.status}
                            </Tag>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Timeout',
            dataIndex: 'defaultTimeoutMs',
            key: 'defaultTimeoutMs',
            render: (timeout?: number) => {
                if (!timeout) return <Text type="secondary">Default</Text>;
                return (
                    <Text style={{ fontSize: '12px' }}>
                        {formatDuration(timeout)}
                    </Text>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: RegisteredJob) => (
                <Space>
                    <Tooltip title="Run job manually">
                        <Button
                            type="primary"
                            size="small"
                            icon={<PlayCircleOutlined />}
                            disabled={record.isLocked}
                            loading={isRunning}
                            onClick={() => onRunJob(record.jobName)}
                        >
                            Run
                        </Button>
                    </Tooltip>
                    <Tooltip title="View execution history">
                        <Button
                            size="small"
                            icon={<HistoryOutlined />}
                            onClick={() => onViewHistory(record.jobName)}
                        >
                            History
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={registeredJobs}
            rowKey="jobName"
            pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} jobs`,
            }}
            scroll={{ x: 1000 }}
            size="small"
            expandable={{
                expandedRowRender: (record) => {
                    if (!record.statistics) return null;

                    const { recentHistory = [] } = record.statistics;
                    if (recentHistory.length === 0) return null;

                    return (
                        <div style={{ margin: 0 }}>
                            <Text strong style={{ fontSize: '12px' }}>Recent Executions:</Text>
                            <div style={{ marginTop: 8 }}>
                                {recentHistory.slice(0, 5).map((execution: any, index: number) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '4px 0',
                                        borderBottom: index < 4 ? '1px solid #f0f0f0' : 'none'
                                    }}>
                                        <div>
                                            <Text style={{ fontSize: '11px' }}>
                                                {moment(execution.startedAt).format('YYYY-MM-DD HH:mm:ss')}
                                            </Text>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {execution.durationMs && (
                                                <Text style={{ fontSize: '11px' }}>
                                                    {formatDuration(execution.durationMs)}
                                                </Text>
                                            )}
                                            <Tag
                                                color={execution.status === 'SUCCESS' ? 'green' : 'red'}
                                                style={{ fontSize: '10px', margin: 0 }}
                                            >
                                                {execution.status}
                                            </Tag>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                },
                rowExpandable: (record) => !!(record.statistics?.recentHistory?.length),
            }}
        />
    );
};